import {Dropbox, dropbox_os} from '@dropbox/api-v2-client';

import {beginAssetUpload, completeAssetUpload, createSite, publishDeployment} from './api';
import {prepareAppAssets, PreparedAppAsset} from './assetManifest';
import {putSignedAsset, SignedAssetUploadInstructions} from './signedAssetUpload';
import {PreparedAppUpload} from './uploadValidation';

export type AppPublishPhase =
  | 'begin_asset_upload'
  | 'upload_asset'
  | 'complete_asset_upload'
  | 'create_site'
  | 'publish_deployment';

export class AppPublishError extends Error {
  constructor(
    readonly phase: AppPublishPhase,
    readonly cause: unknown,
    readonly logicalPath?: string
  ) {
    super(logicalPath ? `App publishing failed for ${logicalPath}` : 'App publishing failed');
    this.name = 'AppPublishError';
  }
}

export type PreparedPublishAsset = PreparedAppAsset & {
  requestToken: string;
};

export type AppPublishAttempt = {
  assets: PreparedPublishAsset[];
  completedAssetRefs: Record<string, dropbox_os.DeploymentAssetRef>;
  createSiteRequestToken: string;
  publishDeploymentRequestToken: string;
  slug: string;
};

export type AppPublishProgress =
  | {phase: 'uploading'; completed: number; total: number; logicalPath?: string}
  | {phase: 'creating_site'}
  | {phase: 'publishing'}
  | {phase: 'complete'};

export type AppPublishResult = {
  deploymentId: string;
  launchUrl?: string;
  manifestHash?: string;
  siteId: string;
  slug: string;
};

export type ExistingSitePublishTarget = {
  expectedCandidateDeploymentId: string;
  siteId: string;
  slug: string;
  spaFallbackEnabled: boolean;
};

type TokenFactory = () => string;
type Fetch = typeof globalThis.fetch;

const createRequestToken = () => globalThis.crypto.randomUUID();

export const prepareAppPublishAttempt = async (
  upload: PreparedAppUpload,
  tokenFactory: TokenFactory = createRequestToken,
  signal?: AbortSignal
): Promise<AppPublishAttempt> => ({
  assets: (await prepareAppAssets(upload, signal)).map((asset) => ({
    ...asset,
    requestToken: tokenFactory(),
  })),
  completedAssetRefs: {},
  createSiteRequestToken: `create-${tokenFactory()}`,
  publishDeploymentRequestToken: `publish-${tokenFactory()}`,
  slug: upload.slug,
});

const abortIfRequested = (signal?: AbortSignal) => {
  if (signal?.aborted) {
    throw signal.reason ?? new DOMException('App publishing was cancelled', 'AbortError');
  }
};

const requiredString = (value: string | undefined, field: string) => {
  if (!value) {
    throw new Error(`Asset upload response is missing ${field}`);
  }
  return value;
};

const signedInstructions = (
  response: dropbox_os.BeginAssetUploadResponse
): SignedAssetUploadInstructions => {
  if (typeof response.content_length !== 'number') {
    throw new Error('Asset upload response is missing content_length');
  }

  return {
    asset_sha256: requiredString(response.asset_sha256, 'asset_sha256'),
    asset_ttl_class: requiredString(response.asset_ttl_class, 'asset_ttl_class'),
    checksum_sha256: requiredString(response.checksum_sha256, 'checksum_sha256'),
    content_length: response.content_length,
    content_type: requiredString(response.content_type, 'content_type'),
    put_url: requiredString(response.put_url, 'put_url'),
  };
};

const uploadAsset = async (
  client: Dropbox,
  asset: PreparedPublishAsset,
  options: {fetch?: Fetch; signal?: AbortSignal}
): Promise<dropbox_os.DeploymentAssetRef> => {
  let beginResponse: dropbox_os.BeginAssetUploadResponse;
  try {
    abortIfRequested(options.signal);
    beginResponse = await beginAssetUpload(client, {
      logical_path: asset.logicalPath,
      size: asset.size,
      sha256: asset.sha256,
      content_type: asset.contentType,
      request_token: asset.requestToken,
    });
  } catch (error) {
    throw new AppPublishError('begin_asset_upload', error, asset.logicalPath);
  }

  try {
    abortIfRequested(options.signal);
    await putSignedAsset(
      {asset, instructions: signedInstructions(beginResponse)},
      {fetch: options.fetch, signal: options.signal}
    );
  } catch (error) {
    throw new AppPublishError('upload_asset', error, asset.logicalPath);
  }

  let completeResponse: dropbox_os.CompleteAssetUploadResponse;
  try {
    abortIfRequested(options.signal);
    completeResponse = await completeAssetUpload(client, {request_token: asset.requestToken});
  } catch (error) {
    throw new AppPublishError('complete_asset_upload', error, asset.logicalPath);
  }

  const assetId = completeResponse.asset?.asset_id;
  if (!assetId) {
    throw new AppPublishError(
      'complete_asset_upload',
      new Error('Completed asset response is missing asset_id'),
      asset.logicalPath
    );
  }
  return {logical_path: asset.logicalPath, asset_id: assetId};
};

const uploadAssets = async (
  client: Dropbox,
  attempt: AppPublishAttempt,
  options: {
    concurrency: number;
    fetch?: Fetch;
    onProgress?: (progress: AppPublishProgress) => void;
    signal?: AbortSignal;
  }
) => {
  const completedAssets = new Array<dropbox_os.DeploymentAssetRef>(attempt.assets.length);
  const completedAssetRefs = attempt.completedAssetRefs;
  const pendingIndices: number[] = [];
  for (const [index, asset] of attempt.assets.entries()) {
    const completedAsset = completedAssetRefs[asset.logicalPath];
    if (completedAsset?.asset_id && completedAsset.logical_path === asset.logicalPath) {
      completedAssets[index] = completedAsset;
    } else {
      pendingIndices.push(index);
    }
  }

  let nextPendingIndex = 0;
  let completed = attempt.assets.length - pendingIndices.length;
  let firstError: unknown;

  const worker = async () => {
    while (nextPendingIndex < pendingIndices.length && !firstError) {
      const index = pendingIndices[nextPendingIndex++];
      const asset = attempt.assets[index];
      try {
        const completedAsset = await uploadAsset(client, asset, options);
        completedAssets[index] = completedAsset;
        completedAssetRefs[asset.logicalPath] = completedAsset;
        completed += 1;
        options.onProgress?.({
          phase: 'uploading',
          completed,
          total: attempt.assets.length,
          logicalPath: asset.logicalPath,
        });
      } catch (error) {
        firstError ??= error;
      }
    }
  };

  options.onProgress?.({
    phase: 'uploading',
    completed,
    total: attempt.assets.length,
  });
  await Promise.all(
    Array.from({length: Math.min(options.concurrency, pendingIndices.length)}, () => worker())
  );
  if (firstError) {
    throw firstError;
  }
  return completedAssets;
};

export const publishApp = async (
  client: Dropbox,
  attempt: AppPublishAttempt,
  options: {
    concurrency?: number;
    existingSite?: ExistingSitePublishTarget;
    fetch?: Fetch;
    onProgress?: (progress: AppPublishProgress) => void;
    signal?: AbortSignal;
  } = {}
): Promise<AppPublishResult> => {
  const concurrency = options.concurrency ?? 4;
  if (!Number.isInteger(concurrency) || concurrency < 1) {
    throw new RangeError('Upload concurrency must be a positive integer');
  }

  const assets = await uploadAssets(client, attempt, {...options, concurrency});

  let siteId: string;
  let slug: string;
  let expectedCandidateDeploymentId: string;
  let spaFallbackEnabled: boolean;
  if (options.existingSite) {
    siteId = options.existingSite.siteId;
    slug = options.existingSite.slug;
    expectedCandidateDeploymentId = options.existingSite.expectedCandidateDeploymentId;
    spaFallbackEnabled = options.existingSite.spaFallbackEnabled;
  } else {
    let site: dropbox_os.CreateSiteResponse;
    try {
      abortIfRequested(options.signal);
      options.onProgress?.({phase: 'creating_site'});
      site = await createSite(client, {
        desired_slug: attempt.slug,
        request_token: attempt.createSiteRequestToken,
      });
    } catch (error) {
      throw new AppPublishError('create_site', error);
    }
    if (!site.site_id || !site.slug) {
      throw new AppPublishError('create_site', new Error('Create site response is incomplete'));
    }
    siteId = site.site_id;
    slug = site.slug;
    expectedCandidateDeploymentId = '';
    spaFallbackEnabled = false;
  }

  let deployment: dropbox_os.DeploymentPublicationResponse;
  try {
    abortIfRequested(options.signal);
    options.onProgress?.({phase: 'publishing'});
    deployment = await publishDeployment(client, {
      site_id: siteId,
      expected_candidate_deployment_id: expectedCandidateDeploymentId,
      request_token: attempt.publishDeploymentRequestToken,
      spa_fallback_enabled: spaFallbackEnabled,
      assets,
    });
  } catch (error) {
    throw new AppPublishError('publish_deployment', error);
  }
  if (!deployment.deployment_id) {
    throw new AppPublishError(
      'publish_deployment',
      new Error('Publish deployment response is missing deployment_id')
    );
  }

  options.onProgress?.({phase: 'complete'});
  // launch_url is not in the current generated client yet. Reading it optionally
  // makes the success action available as soon as App Platform adds it to the
  // publication response and the client is regenerated.
  const responseWithLaunchUrl = deployment as dropbox_os.DeploymentPublicationResponse & {
    launch_url?: string;
  };
  return {
    deploymentId: deployment.deployment_id,
    ...(responseWithLaunchUrl.launch_url ? {launchUrl: responseWithLaunchUrl.launch_url} : {}),
    manifestHash: deployment.manifest_hash,
    siteId,
    slug,
  };
};
